import { Map as ImmutableMap } from 'immutable';
import PropTypes from 'prop-types';
import React, { useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { investmentQuotesV2Selectors } from 'app-components/flux/investment-quotes-v2';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { securitiesActions, securitiesSelectors, securitiesTypes } from 'app-components/flux/securities';
import { transactionsSelectors } from 'app-components/flux/transactions';

import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { openNotificationDialogAction } from 'components/Dialogs/NotificationDialog';
import StdDialog from 'components/Dialogs/StdDialog';
import Dump from 'components/Dump';
import SecurityEditForm from 'components/Forms/SecurityEditForm';
import LoadingView from 'components/LoadingView';
import QButton from 'components/QButton';

export const DIALOG_TYPE_SECURITY_EDIT = 'DIALOG_TYPE_SECURITY_EDIT';

export const openSecurityEditDialogAction = (defaultValue, overrides) =>
  rootUiActions.createDialog(
    rootUiTypes.mkRootUiData({
      // The GUID ensured when a new dialog is opened right after
      // closing one, the timeout doesn't close the new dialog.
      id: `security-edit-dialog#${uuidv4()}`,
      type: DIALOG_TYPE_SECURITY_EDIT,
      allowNesting: true,
      props: ImmutableMap({ defaultValue }),
      ...overrides,
    }),
  );

const SecurityEditDialog = React.memo((props) => {
  const { defaultValue: security, ...rest } = props;
  const dispatch = useDispatch();
  const formikRef = useRef();
  const [disableSubmit, setDisableSubmit] = useState(true);
  const onDirty = useCallback((dirty) => setDisableSubmit(!dirty), [setDisableSubmit]);
  const { merged, txnsCount, holdingsCount } = useSelector(transactionsSelectors.getSecuritiesUsage);
  const manualCreatedSecurityQuoteIsLoading = useSelector((state) => securitiesSelectors.getIsLoading(state));
  const barChartQuoteIsLoading = useSelector((state) => investmentQuotesV2Selectors.getIsLoading(state));
  const quoteIsLoading = barChartQuoteIsLoading || manualCreatedSecurityQuoteIsLoading;
  const securityUsage = merged?.get(security.id);

  return (
    <StdDialog sharedcomponentid={DIALOG_TYPE_SECURITY_EDIT} size='md' title='Security Details' {...rest}>
      <Dump obj={security} />

      <DialogContent>
        <SecurityEditForm
          defaultValue={security}
          formikRef={formikRef}
          onDirty={onDirty}
          setDisableSubmit={setDisableSubmit}
          onClose={(_event, _reason) => dispatch(rootUiActions.removeDialog(props.dialogId))}
        />
      </DialogContent>

      <DialogActions>
        {security?.id && (
          <QButton
            id='security-delete-button'
            variant='delete'
            onClick={() => {
              if (quoteIsLoading) {
                dispatch(
                  openNotificationDialogAction(
                    'Security is in use',
                    <Box display='flex' flexDirection='column' alignItems='center'>
                      <Typography variant='headlineRegular'>
                        Downloading transactions and holdings. Try again.
                      </Typography>
                      <Box marginTop={3} marginBottom={5}>
                        <LoadingView size={100} />
                      </Box>
                    </Box>,
                  ),
                );
              } else if (securityUsage) {
                dispatch(
                  openNotificationDialogAction(
                    'Security is in use',
                    <div>
                      <Typography variant='titleRegular'>
                        {security.name} is currently in use and can&apos;t be deleted. Remove the security from your
                        transactions and holdings in Simplifi to delete it.
                      </Typography>
                      <br />
                      <Typography variant='titleRegular'>
                        {`${txnsCount?.get(security.id)} transaction${
                          txnsCount?.get(security.id) !== 1 ? 's use' : ' uses'
                        } this security.`}
                      </Typography>
                      <br />
                      <Typography variant='titleRegular'>
                        {`${holdingsCount?.get(security.id)} investment holding${
                          holdingsCount?.get(security.id) !== 1 ? 's use' : ' uses'
                        } this security.`}
                      </Typography>
                    </div>,
                  ),
                );
              } else {
                const payload = securitiesTypes.mkSecurity({
                  id: security.id,
                  isDeleted: true,
                });
                dispatch(securitiesActions.deleteSecurity(payload, { undo: { userMessage: 'Security deleted.' } }));
                dispatch(rootUiActions.removeDialog(props.dialogId));
              }
            }}
          >
            Delete
          </QButton>
        )}

        <QButton
          variant='contained'
          disabled={Boolean(disableSubmit)}
          onClick={() => formikRef.current?.submitForm?.()}
          id='security-edit-submit-button'
        >
          {security?.id ? 'Update' : 'Create'}
        </QButton>
      </DialogActions>
    </StdDialog>
  );
});

SecurityEditDialog.displayName = 'SecurityEditDialog';

SecurityEditDialog.propTypes = {
  defaultValue: PropTypes.object, // JS object
  dialogId: PropTypes.string,
};

export default SecurityEditDialog;
